import { useEffect, useState, type ReactNode, type SetStateAction, type Dispatch, type Ref } from "react";

import { SearchOutlined } from "@ant-design/icons";
import { Input, InputRef } from "antd";
import { SizeType } from "antd/lib/config-provider/SizeContext";
import { clsx } from "clsx";

import styles from "./RVInput.module.scss";

export type RVInputProps = {
    /**
     * Callback for input value changes
     */
    action: Dispatch<SetStateAction<string | null>> | Dispatch<string>;
    /**
     * Optionally, a different action for Enter key presses. If not provided, uses `action` prop.
     */
    onPressEnterAction?: Dispatch<SetStateAction<string | null>> | Dispatch<string>;
    /**
     * Placeholder text for the input
     */
    placeholder?: string;
    /**
     * Suffix element for the input
     */
    suffix?: ReactNode;
    /**
     * Autofocus the input on mount
     */
    autoFocus?: boolean;
    /**
     * Name attribute for the input
     */
    name?: string;
    /**
     * Ref for the input element
     */
    inputRef?: Ref<InputRef>;
    /**
     * Element to display after the input
     */
    addonAfter?: JSX.Element;
    /**
     * Size of the input
     */
    size?: SizeType;
    /**
     * Additional className(s)
     */
    className?: string;
    /**
     * Test id for the input
     */
    "data-testid"?: string;
    /**
     * Disable the input
     */
    disabled?: boolean;
    /**
     * Default value for the input
     */
    defaultValue?: string;
    /**
     * Autocomplete attribute
     */
    autocomplete?: string;
};

/**
 * RVInput
 *
 * A basic input component.
 *
 * @param {RVInputProps} props - Component props
 * @returns {JSX.Element}
 */
export function RVInput({
    action,
    onPressEnterAction,
    placeholder,
    suffix,
    autoFocus,
    name,
    inputRef,
    addonAfter,
    size,
    className,
    "data-testid": dataTestId,
    disabled = false,
    defaultValue,
    autocomplete,
}: RVInputProps): JSX.Element {
    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        setValue(defaultValue);
    }, [defaultValue]);

    // we use a spread to conditionally pass a ref to the input if one was provided
    const extraProps: Record<string, unknown> = typeof inputRef === "undefined" ? {} : { ref: inputRef };
    if (name) {
        extraProps.name = name;
    }
    return (
        <Input
            value={value}
            placeholder={placeholder}
            className={clsx(styles.searchInput, className)}
            prefix={<SearchOutlined className={styles.searchIcon} />}
            addonAfter={addonAfter}
            onChange={(e) => {
                e.persist();
                action(e.target.value);
                setValue(e.target.value);
            }}
            onPressEnter={(e) => {
                const handler = onPressEnterAction || action;
                handler(e.currentTarget.value);
            }}
            suffix={suffix}
            autoFocus={autoFocus}
            size={size || "middle"}
            data-testid={dataTestId}
            disabled={disabled}
            autoComplete={autocomplete}
            {...extraProps}
        />
    );
}
